import { Box, Tag, Text } from '@factorialco/deprecated-design-system'
import {
  FormsTypeEnum,
  ProjectionKind,
  useMySurveysDataTable,
} from 'generated/resources/datatable.graphql'
import { MySurveysDatatableTrainingsFilterFragment } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { translateFormType } from '../../../lib/trainingFormTypes'
import { EnrolledEmployees } from '../../Revamp/TrainingsDetail/TrainingEmployeesDataTable/enrolledEmployees'
import { courseFilter } from './Filters/CourseFilter'
import { participantFilter } from './Filters/ParticipantFilter'
import { statusFilter } from './Filters/StatusFilter'
import { formTypeFilter } from './Filters/TypeFilter'

const statusTag = (answerCount: number) => {
  if (answerCount > 0) {
    return (
      <Box width="fit-content">
        <Tag leftItem="circle" type="lime" showCircle ellipsis>
          {i18n.t('trainings.my_surveys.datatable.status.completed')}
        </Tag>
      </Box>
    )
  } else {
    return (
      <Box width="fit-content">
        <Tag leftItem="circle" type="tangerine" showCircle ellipsis>
          {i18n.t('trainings.my_surveys.datatable.status.pending')}
        </Tag>
      </Box>
    )
  }
}
export const MySurveysDatatable = ({
  trainings,
}: {
  trainings: MySurveysDatatableTrainingsFilterFragment[]
}) => {
  const { access } = useCurrentSession()
  const { debouncedSearch, search, onSearch } = useDebouncedSearch()
  const { navigation } = useNavigation()
  const source = useMySurveysDataTable(
    [
      {
        identifier: 'title',
        kind: ProjectionKind.Path,
        title: i18n.t('trainings.my_surveys.datatable.name'),
        path: 'materializedForm_form_title',
      },
      {
        identifier: 'status',
        kind: ProjectionKind.Custom,
        template: '{{status}}',
        title: i18n.t('trainings.my_surveys.datatable.status.label'),
        render: ({ answersConnection_totalCount: answerCount }) => {
          const count = answerCount ?? 0
          return statusTag(count)
        },
      },
      {
        identifier: 'training_name',
        title: i18n.t('trainings.my_surveys.datatable.course'),
        kind: ProjectionKind.Path,
        path: 'materializedForm_resourceName',
      },
      {
        identifier: 'participant',
        title: i18n.t('trainings.my_surveys.datatable.participant'),
        kind: ProjectionKind.Custom,
        template: '{{participant}}',
        render: ({ target }) => {
          return <EnrolledEmployees access={target} />
        },
      },
      {
        identifier: 'formType',
        kind: ProjectionKind.Custom,
        render: ({ materializedForm_formType }) => {
          return (
            <Text.Normal>
              {
                // eslint-disable-next-line no-type-assertion/no-type-assertion
                translateFormType(materializedForm_formType as FormsTypeEnum)
              }
            </Text.Normal>
          )
        },
        template: '{{formType}}',
        title: i18n.t('trainings.surveys.type.column_title'),
      },
    ],
    {
      variables: {
        respondentId: access.id.toString(),
        search: debouncedSearch,
      },
    }
  )

  return (
    <DataTable
      source={source}
      exportFileName="MyTrainingSurveys"
      rowTo={({ id, materializedForm_id }) =>
        navigation.myTrainings.surveys.detail.fullPath({
          membership_id: Number(id),
          materialized_form_id: Number(materializedForm_id),
        })
      }
      filters={[
        statusFilter(source),
        formTypeFilter(source),
        participantFilter(source),
        courseFilter({
          source: source,
          trainings: trainings,
        }),
      ]}
      sort={{ path: 'targetId', direction: 'asc' }}
      search={{
        placeholder: i18n.t('common.search'),
        value: search,
        onChange: onSearch,
      }}
    />
  )
}
